import { useState, useEffect } from 'react';
import axiosInstance from '../../../api/axiosInstance';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from 'recharts';

const C = {
  gray: '#5B6B79', blue: '#4a90d9', red: '#e53e3e',
  orange: '#F29E4C', green: '#2CA87F', purple: '#7c3aed', teal: '#0891b2',
};

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const PIE_COLORS = [C.teal, C.orange, C.purple, C.green, C.blue, C.gray, '#e91e63', '#ff9800'];

const thStyle = {
  fontSize: 11, fontWeight: 700, color: C.gray, textTransform: 'uppercase',
  letterSpacing: '0.05em', padding: '8px 10px', background: '#f8f9fb',
  borderBottom: '1px solid #e8ecf0', whiteSpace: 'nowrap',
};
const tdStyle = {
  fontSize: 13, color: '#2d3748', padding: '7px 10px',
  verticalAlign: 'middle', borderBottom: '1px solid #f0f2f5',
};

function SectionCard({ title, icon, color, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', marginBottom: 20, overflow: 'hidden' }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 18px', background: '#f8f9fb', cursor: 'pointer',
          borderBottom: open ? '1px solid #e8ecf0' : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <i className={`ti ${icon}`} style={{ color, fontSize: 18 }} />
          <span style={{ fontWeight: 700, fontSize: 14, color: '#2d3748' }}>{title}</span>
        </div>
        <i className={`ti ${open ? 'ti-chevron-up' : 'ti-chevron-down'}`} style={{ color: C.gray, fontSize: 14 }} />
      </div>
      {open && <div style={{ padding: '16px 18px' }}>{children}</div>}
    </div>
  );
}

function KpiCard({ label, value, icon, color, sub }) {
  return (
    <div style={{
      flex: 1, minWidth: 160, background: '#fff', borderRadius: 12,
      border: `1px solid ${color}44`, padding: '16px 18px',
      boxShadow: `0 2px 8px ${color}18`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: C.gray }}>{label}</span>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: `${color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className={`ti ${icon}`} style={{ color, fontSize: 16 }} />
        </div>
      </div>
      <div style={{ fontSize: 24, fontWeight: 800, color: '#1a202c' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.gray, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function SimpleTable({ headers, rows, emptyMsg = 'No data' }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>{headers.map((h, i) => <th key={i} style={thStyle}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0
            ? <tr><td colSpan={headers.length} style={{ ...tdStyle, textAlign: 'center', color: C.gray, padding: 20 }}>{emptyMsg}</td></tr>
            : rows}
        </tbody>
      </table>
    </div>
  );
}

export default function Analytics() {
  const today = new Date();
  const thirtyAgo = new Date(today - 30 * 24 * 60 * 60 * 1000);
  const fmt = d => d.toISOString().slice(0, 10);

  const [from, setFrom] = useState(fmt(thirtyAgo));
  const [to, setTo] = useState(fmt(today));
  const [applied, setApplied] = useState({ from: fmt(thirtyAgo), to: fmt(today) });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`getPharmacyAnalytics?from=${applied.from}&to=${applied.to}`)
      .then(res => setData(res.data?.data || null))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, [applied]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
      <div style={{ textAlign: 'center', color: C.gray }}>
        <i className="ti ti-loader-2" style={{ fontSize: 32 }} />
        <div style={{ marginTop: 8, fontSize: 14 }}>Loading analytics...</div>
      </div>
    </div>
  );

  if (!data) return <div style={{ textAlign: 'center', color: C.gray, padding: 40 }}>Failed to load analytics data.</div>;

  const {
    kpis, stock_status = [], stock_by_category = [], dead_stock = [],
    expiry_timeline = [], expiry_risk = [], writeoff_history = [],
    daily_movements = [], movement_breakdown = [], top_consumed = [],
    vendor_spend = [], po_cycle_time = [], pending_pos = [],
    monthly_spend = [], days_of_supply = [],
  } = data;

  // ── Section 2: Stock Health ──
  const stockChartData = stock_status.slice(-15).map(d => ({
    name: d.name.length > 18 ? d.name.slice(0, 18) + '…' : d.name,
    qty: Number(d.quantity),
    reorder: Number(d.reorder_level),
    fill: d.quantity === 0 ? C.red : Number(d.quantity) <= Number(d.reorder_level) ? C.orange : C.green,
  }));

  const categoryChartData = stock_by_category.map((c, i) => ({
    name: c.category,
    value: Number(c.count),
    fill: PIE_COLORS[i % PIE_COLORS.length],
  }));

  // ── Section 3: Expiry ──
  const expiryChartData = expiry_timeline.map(r => ({
    name: `${MONTHS[(r.month - 1)]} ${r.year}`,
    qty: Number(r.qty_at_risk),
  }));

  const writeoffChartData = writeoff_history.map(r => ({
    name: `${MONTHS[(r.month - 1)]} ${r.year}`,
    qty: Number(r.qty),
    value: Number(r.value),
  }));

  // ── Section 4: Consumption ──
  const dailyChartData = daily_movements.map(r => ({
    name: r.date,
    'Stock In': r.stock_in || 0,
    'Stock Out': r.stock_out || 0,
  }));

  const movementPieData = movement_breakdown.map((m, i) => ({
    name: m.type.replace(/_/g, ' '),
    value: Number(m.total_qty),
    fill: PIE_COLORS[i % PIE_COLORS.length],
  }));

  // ── Section 5: Procurement ──
  const vendorChartData = vendor_spend.map(v => ({
    name: v.vendor_name.length > 15 ? v.vendor_name.slice(0, 15) + '…' : v.vendor_name,
    spend: Number(v.total_spend),
  }));

  const monthlySpendData = monthly_spend.map(r => ({
    name: `${MONTHS[(r.month - 1)]} ${r.year}`,
    total: Number(r.total),
  }));

  return (
    <div style={{ padding: '0 4px' }}>

      {/* Date Range Picker */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20,
        background: '#fff', borderRadius: 10, border: '1px solid #e8ecf0',
        padding: '10px 16px', flexWrap: 'wrap',
      }}>
        <i className="ti ti-calendar" style={{ color: C.gray, fontSize: 16 }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: C.gray }}>Date Range:</span>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)}
          style={{ height: 32, fontSize: 13, borderRadius: 6, border: '1px solid #e8ecf0', padding: '0 8px', outline: 'none' }} />
        <span style={{ color: C.gray, fontSize: 12 }}>to</span>
        <input type="date" value={to} onChange={e => setTo(e.target.value)}
          style={{ height: 32, fontSize: 13, borderRadius: 6, border: '1px solid #e8ecf0', padding: '0 8px', outline: 'none' }} />
        <button onClick={() => setApplied({ from, to })} style={{
          height: 32, padding: '0 16px', background: C.teal, color: '#fff',
          border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>Apply</button>
        <span style={{ fontSize: 11, color: C.gray }}>
          (Charts for movements use this range; stock levels and expiry data are always current)
        </span>
      </div>

      {/* Section 1: KPIs */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
        <KpiCard label="Inventory Value" value={`BHD ${Number(kpis.inventory_value).toFixed(3)}`} icon="ti-coin" color={C.teal} sub="Cost × Current Qty" />
        <KpiCard label="Items In Stock" value={kpis.items_in_stock} icon="ti-package" color={C.blue} sub="Drugs with qty > 0" />
        <KpiCard label="Low Stock" value={kpis.low_stock_count} icon="ti-alert-triangle" color={kpis.low_stock_count > 0 ? C.orange : C.green} sub="At or below reorder level" />
        <KpiCard label="Expiring ≤ 30 Days" value={kpis.expiring_soon} icon="ti-clock-exclamation" color={kpis.expiring_soon > 0 ? C.red : C.green} sub="Batches with qty remaining" />
        <KpiCard label="Write-off Loss (MTD)" value={`BHD ${Number(kpis.writeoff_loss_mtd).toFixed(3)}`} icon="ti-trash" color={kpis.writeoff_loss_mtd > 0 ? C.red : C.green} sub="This month" />
      </div>

      {/* Section 2: Stock Health */}
      <SectionCard title="Stock Health" icon="ti-heart-rate-monitor" color={C.green}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#2d3748', marginBottom: 10 }}>Stock Levels vs Reorder Point (Bottom 15)</div>
            {stockChartData.length === 0
              ? <div style={{ color: C.gray, fontSize: 13, padding: 10 }}>No stock data.</div>
              : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stockChartData} layout="vertical" margin={{ left: 10, right: 20, top: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                    <Tooltip formatter={(v, n) => [v, n === 'qty' ? 'Current Stock' : 'Reorder Level']} />
                    <Bar dataKey="qty" name="Current Stock" radius={[0, 4, 4, 0]}>
                      {stockChartData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                    </Bar>
                    <Bar dataKey="reorder" name="Reorder Level" fill={`${C.gray}55`} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )
            }
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#2d3748', marginBottom: 10 }}>Stock by Category</div>
            {categoryChartData.length === 0
              ? <div style={{ color: C.gray, fontSize: 13, padding: 10 }}>No category data.</div>
              : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={categoryChartData} cx="50%" cy="50%" innerRadius={70} outerRadius={110}
                      dataKey="value" nameKey="name" fontSize={11}>
                      {categoryChartData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                    </Pie>
                    <Tooltip formatter={(v) => [v, 'Items']} />
                    <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )
            }
          </div>
        </div>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#2d3748', marginBottom: 8 }}>
          Dead Stock <span style={{ fontWeight: 400, color: C.gray }}>(No consumption in last 90 days)</span>
        </div>
        <SimpleTable
          headers={['Drug Name', 'Qty in Stock', 'Last Movement']}
          emptyMsg="No dead stock — good inventory turnover"
          rows={dead_stock.map((d, i) => (
            <tr key={i}>
              <td style={tdStyle}>{d.name}</td>
              <td style={tdStyle}><span style={{ fontWeight: 700, color: C.orange }}>{d.quantity}</span></td>
              <td style={{ ...tdStyle, color: C.gray }}>{d.last_movement ? new Date(d.last_movement).toLocaleDateString() : 'Never'}</td>
            </tr>
          ))}
        />
      </SectionCard>

      {/* Section 3: Expiry & Batch Risk */}
      <SectionCard title="Expiry & Batch Risk" icon="ti-calendar-exclamation" color={C.red}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#2d3748', marginBottom: 10 }}>Expiry Timeline — Next 12 Months</div>
            {expiryChartData.length === 0
              ? <div style={{ color: C.gray, fontSize: 13, padding: 10 }}>No upcoming expiries. Great inventory management!</div>
              : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={expiryChartData} margin={{ left: 0, right: 10, top: 5, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-40} textAnchor="end" interval={0} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => [v, 'Qty at Risk']} />
                    <Bar dataKey="qty" name="Qty at Risk" fill={C.orange} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )
            }
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#2d3748', marginBottom: 10 }}>Write-off History (Last 12 Months)</div>
            {writeoffChartData.length === 0
              ? <div style={{ color: C.gray, fontSize: 13, padding: 10 }}>No write-offs recorded.</div>
              : (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={writeoffChartData} margin={{ left: 0, right: 10, top: 5, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-40} textAnchor="end" interval={0} />
                    <YAxis yAxisId="qty" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="val" orientation="right" tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line yAxisId="qty" type="monotone" dataKey="qty" name="Units Written Off" stroke={C.red} strokeWidth={2} dot={false} />
                    <Line yAxisId="val" type="monotone" dataKey="value" name="Value Lost (BHD)" stroke={C.orange} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )
            }
          </div>
        </div>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#2d3748', marginBottom: 8 }}>
          Expiry Risk Table <span style={{ fontWeight: 400, color: C.gray }}>(Batches expiring within 90 days)</span>
        </div>
        <SimpleTable
          headers={['Batch #', 'Drug', 'Expiry Date', 'Qty Remaining', 'Days Until Expiry', 'Value at Risk']}
          emptyMsg="No batches expiring within 90 days"
          rows={expiry_risk.map((r, i) => {
            const days = Number(r.days_until_expiry);
            const rowBg = days < 0 ? '#fff5f5' : days <= 30 ? '#fffaf0' : 'transparent';
            const daysBg = days < 0 ? '#fed7d7' : days <= 30 ? '#feebc8' : '#fefcbf';
            const daysColor = days < 0 ? '#742a2a' : days <= 30 ? '#7b341e' : '#744210';
            return (
              <tr key={i} style={{ background: rowBg }}>
                <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{r.batch_number}</td>
                <td style={tdStyle}>{r.drug_name}</td>
                <td style={tdStyle}>{r.expiry_date ? new Date(r.expiry_date).toLocaleDateString() : ''}</td>
                <td style={tdStyle}>{r.quantity_remaining}</td>
                <td style={tdStyle}>
                  <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: daysBg, color: daysColor }}>
                    {days < 0 ? `Expired ${Math.abs(days)}d ago` : `${days}d`}
                  </span>
                </td>
                <td style={{ ...tdStyle, fontWeight: 700, color: C.red }}>BHD {Number(r.value_at_risk).toFixed(3)}</td>
              </tr>
            );
          })}
        />
      </SectionCard>

      {/* Section 4: Consumption Trends */}
      <SectionCard title="Consumption Trends" icon="ti-trending-up" color={C.teal}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 20 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#2d3748', marginBottom: 10 }}>Daily Stock-In vs Stock-Out (Selected Period)</div>
            {dailyChartData.length === 0
              ? <div style={{ color: C.gray, fontSize: 13, padding: 10 }}>No movements in selected period.</div>
              : (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={dailyChartData} margin={{ left: 0, right: 10, top: 5, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-40} textAnchor="end" interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="Stock In" stroke={C.green} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="Stock Out" stroke={C.red} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )
            }
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#2d3748', marginBottom: 10 }}>Movement Type Breakdown</div>
            {movementPieData.length === 0
              ? <div style={{ color: C.gray, fontSize: 13, padding: 10 }}>No movements in selected period.</div>
              : (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={movementPieData} cx="50%" cy="42%" innerRadius={55} outerRadius={82}
                      dataKey="value" nameKey="name" fontSize={11}>
                      {movementPieData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [v + ' units', n]} />
                    <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )
            }
          </div>
        </div>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#2d3748', marginBottom: 8 }}>Top 10 Most Consumed Drugs (Selected Period)</div>
        <SimpleTable
          headers={['Rank', 'Drug Name', 'Total Units Out', 'Dispensing Events', 'Avg / Day']}
          emptyMsg="No consumption data in selected period"
          rows={top_consumed.map((d, i) => (
            <tr key={i}>
              <td style={{ ...tdStyle, fontWeight: 700, color: C.teal, width: 50 }}>#{i + 1}</td>
              <td style={tdStyle}>{d.name}</td>
              <td style={{ ...tdStyle, fontWeight: 700 }}>{d.total_out}</td>
              <td style={tdStyle}>{d.times_dispensed}</td>
              <td style={{ ...tdStyle, color: C.gray }}>{Number(d.avg_per_day).toFixed(2)}</td>
            </tr>
          ))}
        />
      </SectionCard>

      {/* Section 5: Procurement Performance */}
      <SectionCard title="Procurement Performance" icon="ti-shopping-cart" color={C.orange}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#2d3748', marginBottom: 10 }}>Spend by Vendor</div>
            {vendorChartData.length === 0
              ? <div style={{ color: C.gray, fontSize: 13, padding: 10 }}>No received POs yet.</div>
              : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={vendorChartData} margin={{ left: 0, right: 10, top: 5, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => [`BHD ${Number(v).toFixed(3)}`, 'Total Spend']} />
                    <Bar dataKey="spend" name="Spend (BHD)" fill={C.orange} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )
            }
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#2d3748', marginBottom: 10 }}>Monthly Procurement Spend</div>
            {monthlySpendData.length === 0
              ? <div style={{ color: C.gray, fontSize: 13, padding: 10 }}>No spend data.</div>
              : (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={monthlySpendData} margin={{ left: 0, right: 10, top: 5, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" interval={0} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => [`BHD ${Number(v).toFixed(3)}`, 'Spend']} />
                    <Line type="monotone" dataKey="total" name="Total (BHD)" stroke={C.orange} strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              )
            }
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#2d3748', marginBottom: 8 }}>Vendor Performance</div>
            <SimpleTable
              headers={['Vendor', 'Avg Lead Time', 'Fill Rate']}
              emptyMsg="No completed POs"
              rows={po_cycle_time.map((v, i) => (
                <tr key={i}>
                  <td style={tdStyle}>{v.vendor_name}</td>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>{v.avg_lead_days != null ? `${v.avg_lead_days}d` : '—'}</td>
                  <td style={tdStyle}>
                    {v.fill_rate != null ? (
                      <span style={{
                        padding: '2px 8px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                        background: v.fill_rate >= 90 ? '#c6f6d5' : v.fill_rate >= 70 ? '#feebc8' : '#fed7d7',
                        color: v.fill_rate >= 90 ? '#276749' : v.fill_rate >= 70 ? '#7b341e' : '#742a2a',
                      }}>
                        {v.fill_rate}%
                      </span>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            />
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#2d3748', marginBottom: 8 }}>Overdue Purchase Orders</div>
            <SimpleTable
              headers={['PO #', 'Vendor', 'Expected Date', 'Days Overdue']}
              emptyMsg="No overdue POs"
              rows={pending_pos.map((p, i) => (
                <tr key={i}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: 700 }}>{p.po_number}</td>
                  <td style={tdStyle}>{p.vendor_name}</td>
                  <td style={tdStyle}>{p.expected_date ? new Date(p.expected_date).toLocaleDateString() : '—'}</td>
                  <td style={tdStyle}>
                    <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: '#fed7d7', color: '#742a2a' }}>
                      {p.days_overdue}d
                    </span>
                  </td>
                </tr>
              ))}
            />
          </div>
        </div>
      </SectionCard>

      {/* Section 6: Demand Forecasting */}
      <SectionCard title="Demand Forecasting & Reorder Suggestions" icon="ti-crystal-ball" color={C.purple}>
        <div style={{ fontSize: 12, color: C.gray, marginBottom: 12 }}>
          Based on average daily consumption over the last 30 days. Drugs with no recent consumption are excluded.
        </div>
        <SimpleTable
          headers={['Drug', 'In Stock', 'Avg Daily Use', 'Days Left', 'Est. Stockout', 'Order By', 'Preferred Vendor', 'Suggested Qty']}
          emptyMsg="No forecast data — ensure stock movements are being recorded"
          rows={days_of_supply.filter(d => Number(d.days_remaining) < 9999).map((d, i) => {
            const days = Number(d.days_remaining);
            const rowBg = days <= 7 ? '#fff5f5' : days <= 14 ? '#fffaf0' : 'transparent';
            const daysBg = days <= 7 ? '#fed7d7' : days <= 14 ? '#feebc8' : '#c6f6d5';
            const daysColor = days <= 7 ? '#742a2a' : days <= 14 ? '#7b341e' : '#276749';
            return (
              <tr key={i} style={{ background: rowBg }}>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{d.name}</td>
                <td style={tdStyle}>{d.quantity}</td>
                <td style={{ ...tdStyle, color: C.gray }}>{Number(d.avg_daily).toFixed(2)}</td>
                <td style={tdStyle}>
                  <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: daysBg, color: daysColor }}>
                    {days}d
                  </span>
                </td>
                <td style={{ ...tdStyle, fontSize: 12 }}>
                  {d.stockout_date ? new Date(d.stockout_date).toLocaleDateString() : '—'}
                </td>
                <td style={{ ...tdStyle, fontSize: 12, fontWeight: days <= 14 ? 700 : 400, color: days <= 14 ? C.red : '#2d3748' }}>
                  {d.order_by_date ? new Date(d.order_by_date).toLocaleDateString() : '—'}
                </td>
                <td style={{ ...tdStyle, color: C.teal, fontSize: 12 }}>{d.preferred_vendor || '—'}</td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{d.suggested_qty || 0}</td>
              </tr>
            );
          })}
        />
      </SectionCard>
    </div>
  );
}
